import {
  Flex,
  HStack,
  Icon,
  IconButton,
  Text,
  Tooltip,
  useColorModeValue,
  Menu,
  MenuList,
  MenuButton,
  MenuItem,
  useDisclosure,
} from "@chakra-ui/react";

import { SetParameterModal } from "./ConfigModal";

import * as mdIcons from "react-icons/md";
import * as faIcons from "react-icons/fa";

import { ApiDownloadCoverageParameter } from "api/coverage/inputParameters";
import { useLoading } from "contexts/LoadingContext";

export const ParameterDetail = (props: {
  lastUpdate: Date | null;
  lastModifiedBy: string;
  id: number;
  name: string;
  nameString: string;
  origin: string;
  originString: string;
  isConfigurable: boolean;
  isDownloadable: boolean;
  actionVariant?: "menu" | "inline";
  [x: string]: any;
}) => {
  const {
    lastUpdate,
    lastModifiedBy,
    id,
    name,
    nameString,
    origin,
    originString,
    isConfigurable,
    isDownloadable,
    reloader,
    productOptions,
    actionVariant = "menu",
    ...rest
  } = props;

  const canConfigure = isConfigurable;
  const hasTemplate = canConfigure && origin === "mass_load";

  const textColor = useColorModeValue("secondaryGray.500", "white");
  const textHover = useColorModeValue(
    { color: "secondaryGray.900", bg: "unset" },
    { color: "secondaryGray.500", bg: "unset" }
  );
  const iconColor = useColorModeValue("brand.500", "white");
  const bgList = useColorModeValue("white", "whiteAlpha.100");
  const bgShadow = useColorModeValue(
    "14px 17px 40px 4px rgba(112, 144, 176, 0.08)",
    "unset"
  );
  const bgButton = useColorModeValue("secondaryGray.300", "whiteAlpha.100");
  const bgHover = useColorModeValue(
    { bg: "secondaryGray.400" },
    { bg: "whiteAlpha.50" }
  );
  const bgFocus = useColorModeValue(
    { bg: "secondaryGray.300" },
    { bg: "whiteAlpha.100" }
  );

  // Ellipsis modals
  const {
    isOpen: isOpen1,
    onOpen: onOpen1,
    onClose: onClose1,
  } = useDisclosure();

  // Loading CTX
  const { show, hide } = useLoading()

  // Inner modal
  const { isOpen, onOpen, onClose } = useDisclosure();

  // Handle parameter download function
  const handleParameterDownload = async (event: any) => {
    show()
    ApiDownloadCoverageParameter(id).then(response => {
      const url = URL.createObjectURL(response.data);
      const a = document.createElement("a");
      a.href = url;
      let d = new Date()
      a.download = nameString + ' (' + d.toLocaleString() + ').xlsx';
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
      hide()
    })
  }

  const configureControl = isConfigurable ? (
    <>
      <SetParameterModal
        id={id}
        name={name}
        nameString={nameString}
        origin={origin}
        originString={originString}
        isOpen={isOpen}
        reloader={reloader}
        onClose={onClose}
        productOptions={productOptions}
        hasTemplate={hasTemplate}
      />
    </>
  ) : null;

  if (actionVariant === "inline") {
    return (
      <HStack spacing={1} flexShrink={0} {...rest}>
        <Menu isOpen={isOpen1} onClose={onClose1}>
          <Tooltip label="Ver historial de cambios" placement="top" hasArrow>
            <MenuButton
              as={IconButton}
              aria-label="Detalle de actualización"
              icon={<Icon as={mdIcons.MdInfoOutline} />}
              size="sm"
              variant="ghost"
              bg={bgButton}
              _hover={bgHover}
            />
          </Tooltip>
          <MenuList
            w="220px"
            border="transparent"
            backdropFilter="blur(63px)"
            bg={bgList}
            boxShadow={bgShadow}
            borderRadius="16px"
            p="12px"
          >
            <MenuItem transition="0.2s linear" color={textColor} p="6px" borderRadius="8px">
              <Flex align="center">
                <Icon as={mdIcons.MdOutlinePerson} h="16px" w="16px" me="8px" />
                <Text fontSize="sm">{lastModifiedBy}</Text>
              </Flex>
            </MenuItem>
            <MenuItem transition="0.2s linear" p="6px" borderRadius="8px" color={textColor}>
              <Flex align="center">
                <Icon as={faIcons.FaCalendarAlt} h="16px" w="16px" me="8px" />
                <Text fontSize="sm">
                  {lastUpdate != null
                    ? new Date(lastUpdate).toLocaleDateString()
                    : "N/D"}
                </Text>
              </Flex>
            </MenuItem>
            <MenuItem transition="0.2s linear" p="6px" borderRadius="8px" color={textColor}>
              <Flex align="center">
                <Icon as={faIcons.FaClock} h="15px" w="15px" me="8px" />
                <Text fontSize="sm">
                  {lastUpdate != null
                    ? new Date(lastUpdate).toLocaleTimeString()
                    : "N/D"}
                </Text>
              </Flex>
            </MenuItem>
          </MenuList>
        </Menu>
        {isDownloadable ? (
          <Tooltip label="Descargar plantilla o datos" placement="top" hasArrow>
            <IconButton
              aria-label="Descargar"
              icon={<Icon as={mdIcons.MdDownload} />}
              size="sm"
              variant="ghost"
              onClick={handleParameterDownload}
            />
          </Tooltip>
        ) : null}
        {isConfigurable ? (
          <Tooltip label="Configurar parámetro" placement="top" hasArrow>
            <IconButton
              aria-label="Configurar"
              icon={<Icon as={mdIcons.MdOutlineSettings} />}
              size="sm"
              variant="ghost"
              colorScheme="brand"
              onClick={onOpen}
            />
          </Tooltip>
        ) : null}
        {configureControl}
      </HStack>
    );
  }

  return (
    <Menu isOpen={isOpen1} onClose={onClose1}>
      <MenuButton
        alignItems="center"
        justifyContent="center"
        bg={bgButton}
        _hover={bgHover}
        _focus={bgFocus}
        _active={bgFocus}
        w="32px"
        h="32px"
        lineHeight="100%"
        onClick={onOpen1}
        borderRadius="10px"
        {...rest}
      >
        <Icon
          as={mdIcons.MdOutlineMoreHoriz}
          color={iconColor}
          w="20px"
          h="20px"
        />
      </MenuButton>
      <MenuList
        w="150px"
        minW="unset"
        maxW="150px !important"
        border="transparent"
        backdropFilter="blur(63px)"
        bg={bgList}
        boxShadow={bgShadow}
        borderRadius="20px"
        p="15px"
      >
        <MenuItem
          transition="0.2s linear"
          color={textColor}
          p="0px"
          borderRadius="8px"
          _active={{
            bg: "transparent",
          }}
          _focus={{
            bg: "transparent",
          }}
          mb="10px"
        >
          <Flex align="center">
            <Icon as={mdIcons.MdOutlinePerson} h="16px" w="16px" me="8px" />
            <Text fontSize="sm" fontWeight="400">
              {lastModifiedBy}
            </Text>
          </Flex>
        </MenuItem>
        <MenuItem
          transition="0.2s linear"
          p="0px"
          borderRadius="8px"
          color={textColor}
          _active={{
            bg: "transparent",
          }}
          _focus={{
            bg: "transparent",
          }}
          mb="10px"
        >
          <Flex align="center">
            <Icon as={faIcons.FaCalendarAlt} h="16px" w="16px" me="8px" />
            <Text fontSize="sm" fontWeight="400">
              {lastUpdate != null
                ? new Date(lastUpdate).toLocaleDateString()
                : "N/D"}
            </Text>
          </Flex>
        </MenuItem>
        <MenuItem
          transition="0.2s linear"
          p="0px"
          borderRadius="8px"
          color={textColor}
          _active={{
            bg: "transparent",
          }}
          _focus={{
            bg: "transparent",
          }}
          mb={isDownloadable || isConfigurable ? "10px" : "0px"}
        >
          <Flex align="center">
            <Icon as={faIcons.FaClock} h="15px" w="15px" me="8px" />
            <Text fontSize="sm" fontWeight="400">
              {lastUpdate != null
                ? new Date(lastUpdate).toLocaleTimeString()
                : "N/D"}
            </Text>
          </Flex>
        </MenuItem>
        {isDownloadable ? (
          <MenuItem
            transition="0.2s linear"
            color={textColor}
            _hover={textHover}
            p="0px"
            borderRadius="8px"
            _active={{
              bg: "transparent",
            }}
            _focus={{
              bg: "transparent",
            }}
            mb={isConfigurable ? "10px" : "0px"}
          >
            <Flex align="center">
              <Icon as={mdIcons.MdDownload} h="16px" w="16px" me="8px" />
              <Text fontSize="sm" fontWeight="400" onClick={handleParameterDownload}>
                Descargar
              </Text>
            </Flex>
          </MenuItem>
        ) : null}
        {isConfigurable ? (
          <MenuItem
            transition="0.2s linear"
            color={textColor}
            _hover={textHover}
            p="0px"
            borderRadius="8px"
            _active={{
              bg: "transparent",
            }}
            _focus={{
              bg: "transparent",
            }}
          >
            <Flex align="center">
              <Icon as={mdIcons.MdOutlineSettings} h="16px" w="16px" me="8px" />
              <Text fontSize="sm" fontWeight="400" onClick={onOpen}>
                Configurar
              </Text>
              {configureControl}
            </Flex>
          </MenuItem>
        ) : null}
      </MenuList>
    </Menu>
  );
};
